import React, { useState, useEffect } from 'react';
import { slackService } from '../lib/slackService';
import { X, HelpCircle } from 'lucide-react';

interface FeedbackWidgetProps {
  className?: string;
}

const FeedbackWidget: React.FC<FeedbackWidgetProps> = ({ className = '' }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState<'success' | 'error' | ''>('');
  const [isMobile, setIsMobile] = useState(false);

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!feedback.trim()) {
      setMessage('Please enter your feedback before submitting.');
      setMessageType('error');
      return;
    }
    
    setIsSubmitting(true);
    setMessage('');
    
    try {
      // Collect additional metadata
      const metadata = {
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href,
      };

      // Send feedback directly using the Slack service
      const response = await slackService.sendFeedback(feedback, metadata);
      
      if (response.success) {
        setMessage('Thank you for your feedback!');
        setMessageType('success');
        setFeedback('');
        
        // Auto-collapse after successful submission
        setTimeout(() => {
          setIsExpanded(false);
          // Clear message after collapsing
          setTimeout(() => {
            setMessage('');
            setMessageType('');
          }, 300);
        }, 2000);
      } else {
        setMessage(response.error || 'Failed to send feedback. Please try again.');
        setMessageType('error');
      }
    } catch (error) {
      setMessage('Failed to send feedback. Please try again.');
      setMessageType('error');
      console.error('Error submitting feedback:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Close feedback when clicking outside
  const handleClickOutside = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      setIsExpanded(false);
    }
  };

  return (
    <>
      {/* Overlay for desktop */}
      {isExpanded && !isMobile && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-20 z-40"
          onClick={handleClickOutside}
        ></div>
      )}

      {/* Tab button */}
      <div className={`fixed bottom-8 md:bottom-8 right-0 z-50 flex ${className}`}>
        {!isExpanded ? (
          <button
            onClick={() => setIsExpanded(true)}
            className="bg-haus-dark-brown text-white rounded-l-md shadow-md hover:bg-haus-light-olive transition-colors flex flex-col items-center justify-center md:py-6 md:px-3 p-3"
            aria-label="Open feedback form"
          >
            <div className="hidden md:block vertical-text">
              <span className="tracking-wider">FEEDBACK</span>
            </div>
            <div className="md:hidden flex items-center justify-center">
              <HelpCircle size={24} />
            </div>
          </button>
        ) : (
          !isMobile && (
            <div 
              className="bg-white rounded-l-lg shadow-lg p-4 border border-gray-200 w-full max-w-sm flex flex-col paper-texture-heavy animate-slide-in"
              style={{ width: '320px', maxWidth: '90vw' }}
            >
              <div className="flex justify-between items-center mb-2">
                <h3 className="text-lg font-medium">Send Feedback</h3>
                <button
                  type="button"
                  onClick={() => setIsExpanded(false)}
                  className="text-haus-light-white hover:text-white p-1 rounded-full hover:bg-haus-dark-brown/20"
                  aria-label="Close feedback form"
                >
                  <X size={20} />
                </button>
              </div>
              
              {message && (
                <div 
                  className={`mb-3 p-2 text-sm rounded ${
                    messageType === 'success' 
                      ? 'bg-green-50 text-green-700 border border-green-200' 
                      : 'bg-red-50 text-red-700 border border-red-200'
                  }`}
                >
                  {message}
                </div>
              )}
              
              <form onSubmit={handleSubmit} className="flex flex-col flex-1">
                <textarea
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  className="w-full border border-gray-300 rounded p-2 mb-3 min-h-[120px] focus:outline-none focus:ring-1 focus:ring-haus-dark-green"
                  placeholder="Tell us what you think..."
                  disabled={isSubmitting}
                ></textarea>
                
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`bg-haus-dark-green text-white py-2 rounded ${
                    isSubmitting 
                      ? 'opacity-70 cursor-not-allowed' 
                      : 'hover:bg-haus-light-olive'
                  } transition-colors`}
                >
                  {isSubmitting ? 'Sending...' : 'Submit'}
                </button>
              </form>
            </div>
          )
        )}
      </div>

      {/* Mobile-specific modal */}
      {isExpanded && isMobile && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={handleClickOutside}
        >
          <div 
            className="bg-white rounded-lg shadow-lg p-4 border border-gray-200 w-full max-h-[80vh] flex flex-col paper-texture-heavy animate-fade-in"
            onClick={e => e.stopPropagation()}
            style={{ maxWidth: '95vw', marginTop: '-10vh' }}
          >
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-lg font-medium">Send Feedback</h3>
              <button
                type="button"
                onClick={() => setIsExpanded(false)}
                className="text-haus-light-white hover:text-white p-2 rounded-full hover:bg-haus-dark-brown/20"
                aria-label="Close feedback form"
              >
                <X size={20} />
              </button>
            </div>
            
            {message && (
              <div 
                className={`mb-3 p-2 text-sm rounded ${
                  messageType === 'success' 
                    ? 'bg-green-50 text-green-700 border border-green-200' 
                    : 'bg-red-50 text-red-700 border border-red-200'
                }`}
              >
                {message}
              </div>
            )}
            
            <form onSubmit={handleSubmit} className="flex flex-col flex-1">
              <textarea
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                className="w-full border border-gray-300 rounded p-3 mb-4 flex-1 focus:outline-none focus:ring-1 focus:ring-haus-dark-brown text-base"
                placeholder="Tell us what you think..."
                disabled={isSubmitting}
                style={{ minHeight: '150px' }}
              ></textarea>
              
              <div className="mt-auto">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`bg-haus-dark-green text-white py-2 px-5 rounded w-full ${
                    isSubmitting 
                      ? 'opacity-70 cursor-not-allowed' 
                      : 'hover:bg-haus-light-olive'
                  } transition-colors`}
                >
                  {isSubmitting ? 'Sending...' : 'Submit'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default FeedbackWidget; 